import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import profile_h from "../../../images/user.jpg";
import logo from "../../../images/logomob.png";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const BASE_URL = "https://backoffice.rmcrownmarket.com/";

const Header = ({ onNote }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [authenticated, setAuthenticated] = useState(() => !!localStorage.getItem("auth_token"));
  const [profile, setProfile] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginInput, setLoginInput] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);

  const signOutLocally = useCallback(() => {
    localStorage.removeItem("auth_token");
    setAuthenticated(false);
    setProfile(null);
    setAccountOpen(false);
    history.push("/login");
  }, [history]);

  useEffect(() => {
    // Register only once per mount, not on every render.
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && localStorage.getItem("auth_token")) {
          signOutLocally();
          swal("Unauthorized", error.response?.data?.message || "Please log in again.", "warning");
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [signOutLocally]);

  const fetchProfile = useCallback(async () => {
    if (!localStorage.getItem("auth_token")) return;
    try {
      const response = await axios.post("/apps-profile", { key_s: KEY, currency: "USD" });
      setProfile(response?.data?.data || null);
    } catch (error) {
      if (error?.response?.status !== 401) console.error("Profile request failed", error);
    }
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    fetchProfile();
    // Reduced polling from 5 seconds to 30 seconds to lower network load.
    const interval = setInterval(() => {
      if (!document.hidden) fetchProfile();
    }, 30000);
    return () => clearInterval(interval);
  }, [authenticated, fetchProfile]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrors({});
    try {
      const res = await axios.post("/apps-user-login", { ...loginInput, key_s: KEY });
      if (res?.data?.status === "success") {
        localStorage.setItem("auth_token", res.data.accessToken);
        setAuthenticated(true);
        setLoginOpen(false);
        setLoginInput({ username: "", password: "" });
        swal("Success", res.data.message || "Logged in successfully", "success");
        history.push("/");
      } else if (res?.data?.status === "fail") {
        swal("Warning", res.data.message || "Unable to log in", "warning");
      } else {
        setErrors(res?.data?.validation_error || {});
      }
    } catch (error) {
      swal("Error", error?.response?.data?.message || "Login failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      const res = await axios.post("/logout");
      // Preserve original server response behavior, but accept a normal success logout too.
      if (res?.data?.message === "Unauthenticated" || res?.data?.status === "success" || res?.status === 200) {
        signOutLocally();
      }
    } catch (error) {
      if (error?.response?.status === 401) signOutLocally();
      else swal("Error", "Unable to log out. Please try again.", "error");
    }
  };

  const picture = profile?.picture_Url && profile.picture_Url !== "0"
    ? (/^https?:\/\//i.test(profile.picture_Url) ? profile.picture_Url : BASE_URL + String(profile.picture_Url).replace(/^\//, ""))
    : profile_h;
  const balance = Number(profile?.balance || 0);

  return (
    <>
      <style>{`
        html,body,#root{background:#00191D!important}
        .rmh,.rmh *{box-sizing:border-box}
        .rmh{position:sticky;top:0;z-index:1000;width:100%;padding:5px 4px;background:#00191D;color:#fff;font-size:14px}
        .rmh-bar{max-width:1440px;margin:auto;min-height:66px;padding:8px 15px;display:flex;align-items:center;justify-content:space-between;gap:15px;border:1px solid rgba(22,184,199,.24);border-radius:17px;background:linear-gradient(110deg,#00272d,#00191D 65%);box-shadow:0 8px 25px rgba(0,0,0,.14)}
        .rmh-logo{display:flex;align-items:center;flex-shrink:0}.rmh-logo img{width:142px;max-height:46px;object-fit:contain}
        .rmh-search{flex:1;max-width:420px;display:flex;align-items:center;border:1px solid #22525a;background:#00262c;border-radius:12px;overflow:hidden;height:40px}
        .rmh-search input{background:transparent;border:0;outline:0;color:#fff;width:100%;padding:0 12px;font-size:14px}.rmh-search input::placeholder{color:#9bb7bb}
        .rmh-search button{background:transparent;border:0;color:#16B8C7;padding:10px 13px;cursor:pointer}
        .rmh-actions{display:flex;align-items:center;gap:9px;flex-shrink:0}
        .rmh-balance{display:flex;align-items:center;gap:9px;background:#fff;color:#003038;border-radius:12px;padding:8px 13px;min-height:42px;font-weight:800;font-variant-numeric:tabular-nums}
        .rmh-balance i{color:#0698a8}.rmh-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:40px;padding:0 14px;border:0;border-radius:11px;font-size:14px;font-weight:750;cursor:pointer;text-decoration:none!important;transition:transform .18s ease,background .18s ease,box-shadow .18s ease}
        .rmh-btn:hover{transform:translateY(-2px)}.rmh-primary{background:linear-gradient(120deg,#16B8C7,#54e3e6);color:#00242a!important;box-shadow:0 4px 15px rgba(22,184,199,.16)}
        .rmh-outline{border:1px solid #24707a;background:#002b32;color:#fff!important}.rmh-iconbtn{width:42px;padding:0}
        .rmh-avatar{height:42px;width:42px;border-radius:13px;object-fit:cover;border:2px solid #16B8C7}
        .rmh-profile{position:relative}.rmh-profile-trigger{background:none;border:0;padding:0;cursor:pointer;display:flex;align-items:center;gap:5px;color:#16B8C7}
        .rmh-dropdown{position:absolute;top:calc(100% + 10px);right:0;min-width:190px;padding:8px;background:#fff;border-radius:14px;box-shadow:0 12px 36px rgba(0,0,0,.28);animation:rmh-in .18s ease}
        .rmh-dropdown a,.rmh-dropdown button{display:flex;gap:10px;align-items:center;width:100%;padding:12px;border:0;background:transparent;text-decoration:none;color:#00313a;font-size:14px;text-align:left;cursor:pointer;border-radius:9px}
        .rmh-dropdown a:hover,.rmh-dropdown button:hover{background:#e8f8f9}
        .rmh-mobile-search{display:none;padding:9px 4px 2px}.rmh-mobile-search .rmh-search{max-width:none}
        .rmh-overlay{position:fixed;inset:0;z-index:1100;background:rgba(0,12,15,.75);display:flex;align-items:center;justify-content:center;padding:15px}
        .rmh-modal{width:100%;max-width:420px;background:#fff;color:#003039;border-radius:21px;overflow:hidden;box-shadow:0 25px 75px rgba(0,0,0,.35);animation:rmh-in .22s ease}
        .rmh-modalhead{display:flex;justify-content:space-between;align-items:center;padding:20px 22px;background:#00191D;color:#fff}
        .rmh-modalhead h3{font-size:19px;margin:0;font-weight:800}.rmh-close{border:0;background:#12383f;color:#fff;border-radius:9px;width:34px;height:34px;cursor:pointer}
        .rmh-modalbody{padding:23px}.rmh-field{margin-bottom:17px}.rmh-field label{display:block;font-size:14px;font-weight:750;margin-bottom:7px}.rmh-inputrow{display:flex;align-items:center;border:1px solid #d0e3e5;border-radius:11px;overflow:hidden;background:#f7fbfb}.rmh-inputrow>i{padding:0 12px;color:#098f9d}.rmh-inputrow input{width:100%;height:46px;min-width:0;border:0;outline:0;background:transparent;color:#002d34;font-size:14px}.rmh-eye{border:0;background:transparent;color:#008e9b;padding:12px;cursor:pointer}.rmh-error{display:block;color:#c32938;font-size:12px;margin-top:5px}
        .rmh-submit{width:100%;height:47px}.rmh-register{text-align:center;margin-top:15px;color:#53676b}.rmh-register a{color:#008c9b;font-weight:800}
        @keyframes rmh-in{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
        @media(max-width:900px){.rmh-search.desktop{display:none}.rmh-mobile-search{display:block}.rmh-bar{min-height:60px}.rmh-logo img{width:118px}}
        @media(max-width:520px){.rmh-bar{padding:8px;gap:6px}.rmh-logo img{width:99px}.rmh-actions{gap:5px}.rmh-balance{padding:7px 8px;min-height:36px;font-size:12px}.rmh-btn{min-height:36px;padding:0 9px;font-size:12px}.rmh-iconbtn{width:36px}.rmh-avatar{width:36px;height:36px}.rmh-signup-label{display:none}}
        @media(max-width:370px){.rmh-balance{max-width:100px;overflow:hidden}.rmh-logo img{width:82px}}
        @media(prefers-reduced-motion:reduce){.rmh *{animation:none!important;transition:none!important}}
      `}</style>
      <header className="rmh">
        <div className="rmh-bar">
          <Link to="/" className="rmh-logo" aria-label="Home"><img src={logo} alt="RM Crown Market" /></Link>
          <div className="rmh-search desktop">
            <input aria-label="Search" placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            <button type="button" aria-label="Toggle search" onClick={() => setSearchOpen((v) => !v)}><i className="fa-solid fa-magnifying-glass" /></button>
          </div>
          <div className="rmh-actions">
            {authenticated ? (
              <>
                <div className="rmh-balance"><i className="fa-solid fa-wallet" /> ${Number.isFinite(balance) ? balance.toFixed(2) : "0.00"}</div>
                <div className="rmh-profile">
                  <button className="rmh-profile-trigger" type="button" onClick={() => setAccountOpen((v) => !v)} aria-label="Account menu" aria-expanded={accountOpen}>
                    <img className="rmh-avatar" src={picture} alt="Profile" onError={(e) => { e.currentTarget.src = profile_h; }} />
                  </button>
                  {accountOpen && <div className="rmh-dropdown"><Link to="/app-profile" onClick={() => setAccountOpen(false)}><i className="fa-solid fa-user" /> My Profile</Link><button type="button" onClick={handleLogout}><i className="fa-solid fa-right-from-bracket" /> Log out</button></div>}
                </div>
              </>
            ) : (
              <>
                <Link className="rmh-btn rmh-primary" to="/register"><i className="fa-solid fa-user-plus" /><span className="rmh-signup-label">Sign up</span></Link>
                <button type="button" className="rmh-btn rmh-outline rmh-iconbtn" onClick={() => setLoginOpen(true)} aria-label="Log in"><i className="fa-solid fa-right-to-bracket" /></button>
              </>
            )}
            <Link to="/menue" className="rmh-btn rmh-outline rmh-iconbtn" aria-label="Menu"><i className="fa-solid fa-bars" /></Link>
          </div>
        </div>
        {searchOpen && <div className="rmh-mobile-search"><div className="rmh-search"><input placeholder="Search..." aria-label="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)} /><button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search"><i className="fa-solid fa-xmark" /></button></div></div>}
      </header>
      {loginOpen && <div className="rmh-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setLoginOpen(false); }}>
        <section className="rmh-modal" role="dialog" aria-modal="true" aria-label="Log in">
          <div className="rmh-modalhead"><h3><i className="fa-solid fa-shield-halved" style={{color:"#16B8C7",marginRight:9}} />Secure Login</h3><button className="rmh-close" type="button" onClick={() => setLoginOpen(false)} aria-label="Close"><i className="fa-solid fa-xmark" /></button></div>
          <form className="rmh-modalbody" onSubmit={handleLogin}>
            <div className="rmh-field"><label htmlFor="rmh-user">Username</label><div className="rmh-inputrow"><i className="fa-solid fa-user" /><input id="rmh-user" required autoComplete="username" value={loginInput.username} onChange={(e) => setLoginInput((s) => ({...s,username:e.target.value}))} placeholder="Enter username" /></div>{errors.username && <span className="rmh-error">{String(errors.username)}</span>}</div>
            <div className="rmh-field"><label htmlFor="rmh-pass">Password</label><div className="rmh-inputrow"><i className="fa-solid fa-lock" /><input id="rmh-pass" required type={showPassword?"text":"password"} autoComplete="current-password" value={loginInput.password} onChange={(e) => setLoginInput((s) => ({...s,password:e.target.value}))} placeholder="Enter password" /><button className="rmh-eye" type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword?"Hide password":"Show password"}><i className={`fa-solid ${showPassword?"fa-eye-slash":"fa-eye"}`} /></button></div>{errors.password && <span className="rmh-error">{String(errors.password)}</span>}</div>
            <button className="rmh-btn rmh-primary rmh-submit" type="submit" disabled={submitting}>{submitting?"Please wait...":"Login to Account"}</button>
            <div className="rmh-register">Don't have an account? <Link to="/register" onClick={() => setLoginOpen(false)}>Create account</Link></div>
          </form>
        </section>
      </div>}
    </>
  );
};

export default Header;
